/**
 * Modello dell'editor SLA (piano Client F2a, sez. 5).
 *
 * Questo file e' l'unico posto in cui la bozza dell'editor viene costruita,
 * validata e convertita nel payload del PUT: i componenti non toccano i DTO.
 * Le forme di contratto restano quelle di `slaModel.ts` (`SlaDefinitionDto`,
 * `SlaDefinitionInput`); qui non si inventa nessun campo nuovo verso il Server.
 *
 * Regole codificate (sez. 5 F2a e sez. 11.1):
 * - la durata e' **solo relativa** a design-time: `duration_format` e' sempre
 *   `SLA_FORMAT_RELATIVE` in uscita e nessun campo di deadline assoluta esiste
 *   nella bozza;
 * - durata, soglia percentuale residua e responsabile sono obbligatori/limitati
 *   come descritto nei commenti di ogni regola;
 * - l'escalation usa le tre colonne della variazione 2026-09-18 con le stesse
 *   regole della definizione (`isSlaEscalationDelayConfigured`, livelli 1..5,
 *   ruolo NULL = ricaduta sul responsabile).
 */

import {
  SLA_ESCALATION_MAX_LEVELS,
  SLA_ESCALATION_MIN_LEVELS,
  SLA_FORMAT_ABSOLUTE,
  SLA_FORMAT_RELATIVE,
  SLA_TARGET_ACTIVITY,
  SLA_TARGET_PROCESS,
  SLA_UNIT_DAYS,
  SLA_UNIT_HOURS,
  SLA_UNIT_MINUTES,
  SLA_UNIT_WEEKS,
} from "model/slaModel";
import type {
  SlaDefinitionDto,
  SlaDefinitionInput,
  SlaDefinitionsPutRequest,
  SlaDurationFormat,
  SlaDurationUnit,
  SlaTargetType,
} from "model/slaModel";

// ─── Chiavi di traduzione ────────────────────────────────────────────────────

/**
 * Chiavi usate dalla superficie editor. Sono raccolte qui (non sparse nei
 * componenti) perche' il piano sez. 4 le fa concordare con Python P1 in un
 * manifest: `getTranslation` restituisce la chiave se il testo manca, quindi
 * questo elenco e' direttamente il manifest lato editor. Nessun testo e' scritto
 * nel codice e nessuna label usa un enum italiano.
 */
export const SLA_EDITOR_TEXT_KEYS = {
  targetProcess: 'sla.editor.target.process',
  targetActivity: 'sla.editor.target.activity',
  /** Aggiunge lo SLA a un target del modello che ancora non ne ha uno. */
  addTarget: 'sla.editor.target.add',
  /**
   * Rimuove una riga: quelle non salvate spariscono, quelle salvate partono al
   * salvataggio con `active: false`.
   */
  removeTarget: 'sla.editor.target.remove',
  /** Conferma di rimozione di uno SLA salvato; segnaposto `{name}`. */
  removeConfirm: 'sla.editor.target.remove_confirm',
  durationValue: 'sla.editor.duration.value',
  durationUnit: 'sla.editor.duration.unit',
  unitMinutes: 'sla.editor.duration.unit.minutes',
  unitHours: 'sla.editor.duration.unit.hours',
  unitDays: 'sla.editor.duration.unit.days',
  unitWeeks: 'sla.editor.duration.unit.weeks',
  threshold: 'sla.editor.threshold.label',
  thresholdHelp: 'sla.editor.threshold.help',
  responsibleUser: 'sla.editor.responsible.user',
  responsibleNone: 'sla.editor.responsible.none',
  /** Tooltip dell'icona info: quali utenti sono selezionabili come responsabile. */
  responsibleUserInfo: 'sla.editor.responsible.user.info',
  responsibleGroupAdmin: 'sla.editor.responsible.group.admin',
  responsibleGroupHod: 'sla.editor.responsible.group.hod',
  responsibleGroupParticipant: 'sla.editor.responsible.group.participant',
  /** Gruppo del responsabile salvato che non e' piu' tra gli utenti ammessi. */
  responsibleGroupUnavailable: 'sla.editor.responsible.group.unavailable',
  /** Avviso sul campo quando il responsabile salvato non e' piu' ammesso. */
  responsibleNotEligible: 'sla.editor.responsible.not_eligible',
  responsibleNoOptions: 'sla.editor.responsible.no_options',
  responsibleLoading: 'sla.editor.responsible.loading',
  escalationTitle: 'sla.editor.escalation.title',
  escalationAfterWorkingHours: 'sla.editor.escalation.after_working_hours',
  /** Ritardo vuoto = nessuna escalation: semantica del contratto (sez. 11.1). */
  escalationNoDelayHint: 'sla.editor.escalation.after_working_hours.hint',
  escalationLevels: 'sla.editor.escalation.levels',
  escalationRole: 'sla.editor.escalation.role',
  /** Ricaduta sul responsabile quando il ruolo di escalation e' assente (sez. 11.1). */
  escalationRoleFallback: 'sla.editor.escalation.role.fallback',
  /**
   * Configurazione inerte: livelli/ruolo valorizzati senza ritardo (sez. 11.5
   * punto 4). Decisione 11 (2026-09-19) = avviso inline non bloccante
   * (`plans/SLA_Client_Plan.md` sez. 13, opzione B).
   */
  escalationInertWarning: 'sla.editor.escalation.inert_warning',
  /** Comunicazione del gate di organizzazione (sez. 11.1): puo' restare inerte. */
  escalationOrganizationGate: 'sla.editor.escalation.organization_gate',
  /** Policy congelata: le modifiche non escalationo le istanze gia' avviate (sez. 11.1). */
  escalationFrozenPolicy: 'sla.editor.escalation.frozen_policy',
  reload: 'sla.editor.reload',
  conflict: 'sla.editor.revision_conflict',
  warnings: 'sla.editor.warnings',
  fixtureNotice: 'sla.editor.fixture_notice',
} as const;

/** Chiavi di validazione: gli avvisi non bloccano, come il 202 SOFT del Server. */
export const SLA_EDITOR_VALIDATION_KEYS = {
  durationRequired: 'sla.validation.duration_required',
  durationUnitRequired: 'sla.validation.duration_unit_required',
  thresholdRange: 'sla.validation.threshold_range',
  noRecipient: 'sla.validation.no_recipient',
  absoluteFormat: 'sla.validation.absolute_format',
} as const;

// ─── View model della superficie ─────────────────────────────────────────────

/**
 * Target del modello su cui si configura lo SLA. E' un view model della UI (non
 * un DTO): il nome leggibile del nodo non esiste nel payload delle definizioni e
 * lo fornisce il chiamante, che lo ha gia' (element registry del modeler).
 */
export interface SlaEditorTarget {
  targetType: SlaTargetType;
  targetDesignerId: string | null;
  name: string;
}

/** Opzione dei selettori responsabile/escalation: utenti e ruoli dell'organizzazione. */
export interface SlaEditorActorOption {
  id: number;
  name: string;
}

/**
 * Categorie degli utenti ammessi come responsabile dello SLA, in ordine di
 * priorita' (un utente in piu' categorie compare nella prima):
 * - `admin`: amministratori dell'organizzazione (`GET organizations/{id}/admins`);
 * - `hod`: capi reparto, cioe' `users_groups.hod` (`GET groups/{id}/users`);
 * - `participant`: utenti coinvolti nel processo, assegnati alle lane del
 *   modello direttamente o tramite ruolo/gruppo (extension elements).
 * `unavailable` e' solo il responsabile **salvato** che non rientra piu' in
 * nessuna categoria: resta visibile e segnalato, non viene scartato.
 */
export type SlaOwnerCategory = 'admin' | 'hod' | 'participant' | 'unavailable';

export const SLA_OWNER_CATEGORY_ORDER: readonly SlaOwnerCategory[] = [
  'admin',
  'hod',
  'participant',
  'unavailable',
];

export const SLA_OWNER_CATEGORY_LABEL_KEYS: Record<SlaOwnerCategory, string> = {
  admin: SLA_EDITOR_TEXT_KEYS.responsibleGroupAdmin,
  hod: SLA_EDITOR_TEXT_KEYS.responsibleGroupHod,
  participant: SLA_EDITOR_TEXT_KEYS.responsibleGroupParticipant,
  unavailable: SLA_EDITOR_TEXT_KEYS.responsibleGroupUnavailable,
};

/** Opzione del selettore del responsabile, con la categoria che la rende ammessa. */
export interface SlaOwnerOption extends SlaEditorActorOption {
  category: SlaOwnerCategory;
}

export const slaEditorTargetKey = (
  target: Pick<SlaEditorTarget, 'targetType' | 'targetDesignerId'>,
): string => `${target.targetType}:${target.targetDesignerId ?? ''}`;

/** Righe dei tre stati "nessuna selezione". `null` = responsabile/ruolo assente. */
export const SLA_EDITOR_NO_ACTOR = '';

// ─── Bozza per target ────────────────────────────────────────────────────────

/**
 * Bozza editabile di una definizione. Non contiene `duration_format` in uscita:
 * a design-time la durata e' sempre relativa (sez. 5 F2a), quindi il campo non e'
 * esposto all'utente. `sourceDurationFormat` conserva invece il formato **letto**
 * dal Server, per non riscrivere in silenzio una definizione assoluta storica.
 */
export interface SlaTargetDraft {
  targetType: SlaTargetType;
  targetDesignerId: string | null;
  durationValue: number | null;
  durationUnit: SlaDurationUnit | null;
  riskRemainingPercent: number | null;
  responsibleUserId: number | null;
  escalationAfterWorkingHours: number | null;
  escalationLevels: number;
  escalationRoleId: number | null;
  sourceDurationFormat: SlaDurationFormat;
}

/** Unita' ammesse da `duration_unit` (Server: SlaInstance.pas / SlaDefinition.pas). */
export const SLA_DURATION_UNITS: readonly SlaDurationUnit[] = [
  SLA_UNIT_MINUTES,
  SLA_UNIT_HOURS,
  SLA_UNIT_DAYS,
  SLA_UNIT_WEEKS,
];

/** Chiave di traduzione della label di ciascuna unita', senza concatenazioni. */
export const SLA_DURATION_UNIT_LABEL_KEYS: Record<SlaDurationUnit, string> = {
  [SLA_UNIT_MINUTES]: SLA_EDITOR_TEXT_KEYS.unitMinutes,
  [SLA_UNIT_HOURS]: SLA_EDITOR_TEXT_KEYS.unitHours,
  [SLA_UNIT_DAYS]: SLA_EDITOR_TEXT_KEYS.unitDays,
  [SLA_UNIT_WEEKS]: SLA_EDITOR_TEXT_KEYS.unitWeeks,
};

const SLA_THRESHOLD_MIN = 0;
const SLA_THRESHOLD_MAX = 100;

export const slaTargetDraftFromDefinition = (
  dto: SlaDefinitionDto,
): SlaTargetDraft => ({
  targetType: dto.targettype,
  targetDesignerId: dto.targetdesignerid,
  durationValue: dto.durationvalue,
  durationUnit: dto.durationunit,
  riskRemainingPercent: dto.riskremainingpercent,
  responsibleUserId: dto.responsibleuserid,
  escalationAfterWorkingHours: dto.escalationafterworkinghours,
  escalationLevels: dto.escalationlevels,
  escalationRoleId: dto.escalationroleid,
  sourceDurationFormat: dto.durationformat,
});

/**
 * Valori iniziali di un target nuovo presi dalle impostazioni SLA
 * dell'organizzazione. Delle impostazioni (`SlaSettingsDto`) solo la soglia di
 * rischio e' un default per target: durata, unita' ed escalation non hanno un
 * default di organizzazione nel contratto. La soglia entra solo se e' nel range
 * accettato dal Server (1..99, `ValidateDefinitionBlocking`).
 */
export interface SlaTargetDraftDefaults {
  riskRemainingPercent?: number | null;
}

export const slaTargetDraftDefaultsFromSettings = (
  settings: { riskremainingpercent?: number | null } | null | undefined,
): SlaTargetDraftDefaults => {
  const risk = settings?.riskremainingpercent;
  return {
    riskRemainingPercent:
      typeof risk === 'number' && Number.isInteger(risk) && risk >= 1 && risk <= 99
        ? risk
        : null,
  };
};

/**
 * Bozza vuota per un target ancora senza definizione. `escalationLevels` parte
 * dal DEFAULT della colonna (1) e non da un valore inventato; `defaults` sono
 * i valori di organizzazione (`slaTargetDraftDefaultsFromSettings`).
 */
export const emptySlaTargetDraft = (
  target: Pick<SlaEditorTarget, 'targetType' | 'targetDesignerId'>,
  defaults: SlaTargetDraftDefaults = {},
): SlaTargetDraft => ({
  targetType: target.targetType,
  targetDesignerId: target.targetDesignerId,
  durationValue: null,
  durationUnit: null,
  riskRemainingPercent: defaults.riskRemainingPercent ?? null,
  responsibleUserId: null,
  escalationAfterWorkingHours: null,
  escalationLevels: SLA_ESCALATION_MIN_LEVELS,
  escalationRoleId: null,
  sourceDurationFormat: SLA_FORMAT_RELATIVE,
});

/**
 * Bozza -> elemento di `definitions[]` del PUT. `duration_format` e' costante
 * relativa: la scelta assoluta non e' rappresentabile a design-time (sez. 5 F2a).
 */
export const slaTargetDraftToDefinitionInput = (
  draft: SlaTargetDraft,
): SlaDefinitionInput => ({
  target_type: draft.targetType,
  target_designerid: draft.targetDesignerId,
  duration_format: SLA_FORMAT_RELATIVE,
  duration_value: draft.durationValue,
  duration_unit: draft.durationUnit,
  risk_remaining_percent: draft.riskRemainingPercent,
  responsible_user_id: draft.responsibleUserId,
  // Il ruolo responsabile non e' piu' configurabile dal designer (2026-10-08):
  // si invia sempre NULL. Il Server lo accetta (colonna nullable, nessuna
  // validazione sul destinatario) e la notifica ricade su lane/assegnatario.
  responsible_role_id: null,
  escalation_after_working_hours: draft.escalationAfterWorkingHours,
  escalation_levels: draft.escalationLevels,
  escalation_role_id: draft.escalationRoleId,
});

/**
 * `drafts` sono gli upsert; `removed` sono le definizioni salvate da disattivare
 * (`active: false`). Gli elementi non inviati restano invariati sul Server.
 */
export const buildSlaDefinitionsPutRequest = (
  modelRevision: number,
  expectedRevision: number,
  drafts: SlaTargetDraft[],
  removed: SlaTargetDraft[] = [],
): SlaDefinitionsPutRequest => ({
  model_revision: modelRevision,
  expected_revision: expectedRevision,
  definitions: [
    ...drafts.map(slaTargetDraftToDefinitionInput),
    ...removed.map((draft) => ({
      ...slaTargetDraftToDefinitionInput(draft),
      active: false,
    })),
  ],
});

// ─── Regole ──────────────────────────────────────────────────────────────────

/**
 * Livelli di escalation entro il CHECK `BETWEEN 1 AND 5` della migrazione
 * 20260918: i controlli numerici della UI possono produrre valori fuori range
 * (testo vuoto, incolla), quindi vengono riportati dentro invece di essere
 * inviati e rifiutati.
 */
export const clampSlaEscalationLevels = (levels: number): number => {
  if (!Number.isFinite(levels)) return SLA_ESCALATION_MIN_LEVELS;
  const rounded = Math.trunc(levels);
  if (rounded < SLA_ESCALATION_MIN_LEVELS) return SLA_ESCALATION_MIN_LEVELS;
  if (rounded > SLA_ESCALATION_MAX_LEVELS) return SLA_ESCALATION_MAX_LEVELS;
  return rounded;
};

/** true quando il target ha i dati minimi per produrre uno SLA (durata > 0). */
export const isSlaTargetDraftConfigured = (draft: SlaTargetDraft): boolean =>
  draft.durationValue !== null && draft.durationValue > 0;

/**
 * Il target ha i campi che, senza ritardo, non producono mai un'escalation:
 * `escalation_levels > 1` oppure `escalation_role_id` valorizzati con
 * `escalation_after_working_hours` NULL (sez. 11.5 punto 4).
 *
 * ── Decisione 11 (2026-09-19) = opzione B ──────────────────────────────────
 * `plans/SLA_Client_Plan.md` riga 215 / sez. 11.5 punto 4 e sez. 13: lo schema
 * consente questa combinazione e non e' un errore di DB. Approvata l'opzione B:
 * i campi restano abilitati (nessuna disabilitazione, nessun blocco al
 * salvataggio) e `SlaEscalationFields` mostra l'avviso non bloccante
 * `SLA_EDITOR_TEXT_KEYS.escalationInertWarning` quando questo predicato e' true.
 * La chiave va concordata con Python P1 (manifest F1).
 * ───────────────────────────────────────────────────────────────────────────
 */
export const isSlaEscalationInert = (draft: SlaTargetDraft): boolean =>
  draft.escalationAfterWorkingHours === null &&
  (draft.escalationLevels > SLA_ESCALATION_MIN_LEVELS ||
    draft.escalationRoleId !== null);

export interface SlaTargetDraftValidation {
  /**
   * Bloccano il salvataggio: sono guardie di forma della UI (durata mancante,
   * unita' assente, percentuale fuori intervallo). Non sostituiscono la
   * validazione del Server, che resta autoritativa e SOFT (202 + warnings).
   */
  errors: string[];
  /** Avvisi non bloccanti, mostrati accanto alla riga e dopo il salvataggio. */
  warnings: string[];
}

/**
 * Validazione locale della bozza. La configurazione inerte dell'escalation
 * **non** entra qui: la decisione 11 (sez. 13) ha scelto l'avviso inline nel
 * componente (opzione B), non la validazione soft di riga (opzione C), quindi
 * nessuna chiave `sla.validation.*` viene aggiunta per questo caso.
 */
export const validateSlaTargetDraft = (
  draft: SlaTargetDraft,
): SlaTargetDraftValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!isSlaTargetDraftConfigured(draft)) {
    errors.push(SLA_EDITOR_VALIDATION_KEYS.durationRequired);
  }
  if (draft.durationUnit === null) {
    errors.push(SLA_EDITOR_VALIDATION_KEYS.durationUnitRequired);
  }
  if (
    draft.riskRemainingPercent !== null &&
    (!Number.isFinite(draft.riskRemainingPercent) ||
      draft.riskRemainingPercent < SLA_THRESHOLD_MIN ||
      draft.riskRemainingPercent > SLA_THRESHOLD_MAX)
  ) {
    errors.push(SLA_EDITOR_VALIDATION_KEYS.thresholdRange);
  }
  if (draft.responsibleUserId === null) {
    // Configurazione valida ma senza responsabile: avviso, non errore.
    warnings.push(SLA_EDITOR_VALIDATION_KEYS.noRecipient);
  }
  if (draft.sourceDurationFormat === SLA_FORMAT_ABSOLUTE) {
    // Il Server aveva una durata assoluta; il salvataggio la riscrive relativa.
    warnings.push(SLA_EDITOR_VALIDATION_KEYS.absoluteFormat);
  }

  return { errors, warnings };
};

export const isSlaTargetDraftValid = (draft: SlaTargetDraft): boolean =>
  validateSlaTargetDraft(draft).errors.length === 0;

/** Confronto usato per il dirty tracking dell'editor (autosave/undo lato host). */
export const isSlaTargetDraftEqual = (
  a: SlaTargetDraft,
  b: SlaTargetDraft,
): boolean =>
  a.targetType === b.targetType &&
  a.targetDesignerId === b.targetDesignerId &&
  a.durationValue === b.durationValue &&
  a.durationUnit === b.durationUnit &&
  a.riskRemainingPercent === b.riskRemainingPercent &&
  a.responsibleUserId === b.responsibleUserId &&
  a.escalationAfterWorkingHours === b.escalationAfterWorkingHours &&
  a.escalationLevels === b.escalationLevels &&
  a.escalationRoleId === b.escalationRoleId;

export const isSlaDraftListEqual = (
  a: SlaTargetDraft[],
  b: SlaTargetDraft[],
): boolean =>
  a.length === b.length &&
  a.every((draft, index) => isSlaTargetDraftEqual(draft, b[index]));

// ─── Adattamento dalle risposte del Server ───────────────────────────────────

/**
 * `meta.model_revision` e' serializzato come stringa dal controller: il numero
 * serve per `model_revision`/`expected_revision` del PUT, quindi la conversione
 * e' esplicita e fallisce con `null` invece di produrre `NaN`.
 */
export const slaRevisionFromMeta = (meta: { model_revision: string }): number | null => {
  const revision = Number(meta.model_revision);
  return Number.isFinite(revision) && revision > 0 ? revision : null;
};

/** true per il target di processo: il nome lo fornisce il chiamante. */
export const isSlaProcessTarget = (targetType: SlaTargetType): boolean =>
  targetType === SLA_TARGET_PROCESS;

// ─── Designer (pannello proprieta' del modeler) ──────────────────────────────

/**
 * Tipi BPMN ammessi come target SLA di attivita': specchio di
 * `SLA_ALLOWED_ACTIVITY_TYPES_SQL` del Server (`Service.SLA.pas`: task normale,
 * user, manual, service, script). Esclusi send, receive, business rule, call
 * activity e sottoprocesso: il PUT li rifiuterebbe con
 * `sla_target_node_type_not_allowed`.
 */
export const SLA_ALLOWED_BPMN_ACTIVITY_TYPES: readonly string[] = [
  'bpmn:Task',
  'bpmn:UserTask',
  'bpmn:ManualTask',
  'bpmn:ServiceTask',
  'bpmn:ScriptTask',
];

/** Target di processo del designer: il nome e' quello del modello. */
export const slaProcessTarget = (name: string): SlaEditorTarget => ({
  targetType: SLA_TARGET_PROCESS,
  targetDesignerId: null,
  name,
});

/**
 * Target di attivita' dall'elemento selezionato nel modeler (`designerid` = id
 * BPMN). `null` quando il tipo non e' ammesso come target SLA.
 */
export const slaActivityTargetFromElement = (element: {
  id: string;
  type: string;
  businessObject?: { name?: string };
}): SlaEditorTarget | null => {
  if (!element || !SLA_ALLOWED_BPMN_ACTIVITY_TYPES.includes(element.type)) return null;
  return {
    targetType: SLA_TARGET_ACTIVITY,
    targetDesignerId: element.id,
    name: (element.businessObject?.name || '').trim() || element.id,
  };
};

// ─── Sezioni del dettaglio ───────────────────────────────────────────────────

/**
 * Chiavi delle sezioni della card del target; il testo viene solo dal catalogo
 * (`useSlaEditorText`).
 */
export const SLA_EDITOR_TABLE_TEXT_KEYS = {
  sectionDeadline: 'sla.editor.section.deadline',
  sectionRisk: 'sla.editor.section.risk',
  sectionResponsible: 'sla.editor.section.responsible',
} as const;
